/* ==========================================================================
   <doubles-builder> — v0.0.073 — kit builder for two athletes
   Data comes from the section's inline JSON (families → colourways →
   variants). State per athlete: { gender, top: pick, bottom: pick } where
   pick = { family, handle, variantId }. The URL hash keeps the kit
   shareable; adding sends all pieces to the cart in one request.
   ========================================================================== */
const Theme = window.Theme || { routes: {}, settings: {}, strings: {} };
const money = (cents) => (Theme.formatMoney ? Theme.formatMoney(cents) : `${(cents / 100).toFixed(2)}`);
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL'];
const sizeRank = (s) => {
  const i = SIZE_ORDER.indexOf(String(s).toUpperCase().trim());
  return i < 0 ? 100 + String(s).length : i;
};
const esc = (s = '') => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

class DoublesBuilder extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-doubles-data]');
    if (!raw) return;
    try { this.data = JSON.parse(raw.textContent); } catch { return; }
    this.families = this.data.families || [];
    if (!this.families.length) return;
    this.showGender = this.dataset.genders === 'true';
    this.outfit = this.dataset.outfit === 'true' && !!this.dataset.filesBase;
    // which piece of one range answers which piece of the other (men=women)
    this.matchMap = { m2w: {}, w2m: {} };
    (this.dataset.matchPairs || '').split(',').forEach((pair) => {
      const [m, w] = pair.split('=').map((x) => (x || '').trim().toLowerCase());
      if (m && w) { this.matchMap.m2w[m] = w; this.matchMap.w2m[w] = m; }
    });
    this.outfitCache = new Map(); // url → 'ok' | 'missing'
    this.athletes = [...this.querySelectorAll('[data-athlete]')].map((el, i) => ({
      i, el, name: this.data.athletes[i],
      state: { gender: null, top: null, bottom: null },
      pref: { top: null, bottom: null }, // last size the athlete chose, kept across colour/product changes
    }));
    this.bar = this.querySelector('[data-bar]');
    // Collapsible kit summary: remembered per browser; phones start collapsed
    const toggle = this.querySelector('[data-bar-toggle]');
    const summary = this.querySelector('[data-bar-summary]') || this.querySelector('[data-bar-items]');
    if (toggle && summary) {
      let open;
      try { open = localStorage.getItem('dbl-summary'); } catch { open = null; }
      open = open == null ? !window.matchMedia('(max-width: 989px)').matches : open === '1';
      const apply = () => { summary.hidden = !open; toggle.setAttribute('aria-expanded', String(open)); };
      apply();
      toggle.addEventListener('click', () => { open = !open; apply(); try { localStorage.setItem('dbl-summary', open ? '1' : '0'); } catch { /* private mode */ } });
      // close button in the panel's corner
      this.querySelector('[data-bar-close]')?.addEventListener('click', () => { open = false; apply(); try { localStorage.setItem('dbl-summary', '0'); } catch { /* private mode */ } });
    }
    this.addBtn = this.querySelector('[data-add-kit]');
    this.addLabel = this.addBtn ? this.addBtn.textContent.trim() : '';

    // default genders: athlete 1 women, athlete 2 men when both ranges exist
    const genders = new Set(this.families.map((f) => f.gender));
    const hasW = genders.has('women'), hasM = genders.has('men');
    this.athletes[0].state.gender = hasW ? 'women' : hasM ? 'men' : 'unisex';
    this.athletes[1].state.gender = hasM ? 'men' : hasW ? 'women' : 'unisex';

    this.restore();
    this.athletes.forEach((a) => {
      if (!a.state.top) a.state.top = this.firstPick(a, 'top');
      if (!a.state.bottom) a.state.bottom = this.firstPick(a, 'bottom');
    });

    this.bind();
    this.athletes.forEach((a) => this.renderAthlete(a));
    this.renderBar();
    // warm the cache for the other colours of the pieces on screen, so
    // switching colour shows the new photo without a wait
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 900));
    idle(() => this.prefetchNeighbours());
  }

  prefetchNeighbours() {
    if (!this.outfit) return;
    const urls = new Set();
    this.athletes.forEach((a) => {
      const ft = a.state.top && this.family(a.state.top.family);
      const fb = a.state.bottom && this.family(a.state.bottom.family);
      if (!ft || !fb) return;
      ft.colorways.forEach((tc) => fb.colorways.forEach((bc) => {
        const key = this.outfitKeyFor(a.state.gender, ft, fb, tc, bc);
        if (!key || this.outfitCache.has(key)) return;
        urls.add(this.outfitCandidates(key)[0]);
      }));
    });
    [...urls].slice(0, 16).forEach((u) => { const im = new Image(); im.decoding = 'async'; im.src = u; });
  }

  /* ---------- data helpers ---------- */
  familiesFor(a, cat) {
    const g = a.state.gender;
    return this.families.filter((f) => f.category === cat && (!this.showGender || f.gender === 'unisex' || g === 'unisex' || f.gender === g));
  }
  family(key) { return this.families.find((f) => f.key === key); }
  colorway(pick) { return pick ? this.family(pick.family)?.colorways.find((c) => c.handle === pick.handle) : null; }
  variant(pick) { const cw = this.colorway(pick); return cw && pick.variantId ? cw.variants.find((v) => v.id === pick.variantId) : null; }
  firstPick(a, cat) {
    const f = this.familiesFor(a, cat)[0];
    if (!f) return null;
    const cw = f.colorways.find((c) => c.available) || f.colorways[0];
    return { family: f.key, handle: cw.handle, variantId: this.resolveSize(cw, a.pref[cat]) };
  }
  /* Size to preselect for a colourway: the athlete's preferred size when in
     stock, otherwise the nearest available one (ties go up a size); with no
     preference yet, M — or the middle of what is available. */
  resolveSize(cw, pref) {
    const sizes = this.sizesOf(cw).filter((v) => v.available);
    if (!sizes.length) return null;
    if (pref) {
      const exact = sizes.find((v) => v.size === pref);
      if (exact) return exact.id;
      const r = sizeRank(pref);
      const nearest = [...sizes].sort((x, y) => Math.abs(sizeRank(x.size) - r) - Math.abs(sizeRank(y.size) - r) || sizeRank(y.size) - sizeRank(x.size))[0];
      return nearest.id;
    }
    const m = sizes.find((v) => String(v.size).toUpperCase() === 'M');
    return (m || sizes[Math.floor((sizes.length - 1) / 2)]).id;
  }
  sizesOf(cw) {
    // one chip per size, sorted the canonical way
    const map = new Map();
    cw.variants.forEach((v) => { if (!map.has(v.size) || (v.available && !map.get(v.size).available)) map.set(v.size, v); });
    return [...map.values()].sort((x, y) => sizeRank(x.size) - sizeRank(y.size));
  }

  /* ---------- events ---------- */
  bind() {
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-athlete-tab]');
      if (tab) { this.activate(Number(tab.dataset.athleteTab)); return; }
      const fig = e.target.closest('[data-figure]');
      if (fig && window.matchMedia('(max-width: 989px)').matches) { this.activate(Number(fig.closest('[data-athlete]').dataset.athlete)); return; }
      // size guide: open on the tab of this athlete's range (the modal itself opens via theme.js)
      const sg = e.target.closest('[data-dbl-size-guide]');
      if (sg) {
        const i = Number(sg.closest('[data-athlete]')?.dataset.athlete || 0);
        const g = this.athletes[i]?.state.gender;
        const radio = document.getElementById(g === 'men' ? 'dsg-men' : 'dsg-women');
        if (radio) radio.checked = true;
      }
      const gender = e.target.closest('[data-gender]');
      if (gender) { this.setGender(this.athleteOf(gender), gender.dataset.gender); return; }
      const fam = e.target.closest('[data-family]');
      if (fam) { this.setFamily(this.athleteOf(fam), fam.dataset.category, fam.dataset.family); return; }
      const col = e.target.closest('[data-color]');
      if (col) { this.setColor(this.athleteOf(col), col.dataset.category, col.dataset.color); return; }
      const size = e.target.closest('[data-size]');
      if (size && !size.disabled) { this.setSize(this.athleteOf(size), size.dataset.category, Number(size.dataset.size)); return; }
      const copy = e.target.closest('[data-copy-from]');
      if (copy) { this.copyFrom(Number(copy.dataset.copyFrom), this.athleteOf(copy)); return; }
      if (e.target.closest('[data-add-kit]')) this.addKit();
    });
  }
  athleteOf(el) { return this.athletes[Number(el.closest('[data-athlete]').dataset.athlete)]; }

  activate(i) {
    this.athletes.forEach((a) => a.el.classList.toggle('is-active', a.i === i));
    this.querySelectorAll('[data-athlete-tab]').forEach((t) => {
      const on = Number(t.dataset.athleteTab) === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
  }
  setGender(a, gender) {
    if (a.state.gender === gender) return;
    a.state.gender = gender;
    a.state.top = this.firstPick(a, 'top');
    a.state.bottom = this.firstPick(a, 'bottom');
    this.renderAthlete(a);
    this.afterChange();
  }
  setFamily(a, cat, key) {
    if (a.state[cat]?.family === key) return;
    const f = this.family(key);
    // keep the colour if this family has it too
    const prevColor = this.colorway(a.state[cat])?.color?.toLowerCase();
    const cw = f.colorways.find((c) => c.color.toLowerCase() === prevColor && c.available) || f.colorways.find((c) => c.available) || f.colorways[0];
    a.state[cat] = { family: key, handle: cw.handle, variantId: this.resolveSize(cw, a.pref[cat]) };
    this.renderStep(a, cat);
    this.renderFigure(a);
    this.afterChange();
  }
  setColor(a, cat, handle) {
    const pick = a.state[cat];
    if (!pick || pick.handle === handle) return;
    // the size stays; if this colourway lacks it, the nearest in-stock size
    pick.handle = handle;
    pick.variantId = this.resolveSize(this.colorway(pick), a.pref[cat]);
    this.renderStep(a, cat);
    this.renderFigure(a);
    this.afterChange();
  }
  setSize(a, cat, variantId) {
    const pick = a.state[cat];
    if (!pick) return;
    pick.variantId = variantId;
    a.pref[cat] = this.variant(pick)?.size || a.pref[cat];
    this.renderStep(a, cat);
    this.afterChange();
  }
  /* Match: the same kit translated into this athlete's own range — a men's
     tee becomes a women's tank, a men's tank becomes a bra, bottoms keep
     their kind. Colour is kept when the counterpart has it; sizes stay. */
  copyFrom(fromIdx, a) {
    const src = this.athletes[fromIdx];
    ['top', 'bottom'].forEach((cat) => {
      const pick = src.state[cat];
      const target = this.matchFamily(pick, src.state.gender, a, cat);
      if (!target) { a.state[cat] = null; return; }
      const wanted = this.colorway(pick)?.color?.toLowerCase();
      const cw = target.colorways.find((c) => c.color.toLowerCase() === wanted && c.available)
        || target.colorways.find((c) => c.color.toLowerCase() === wanted)
        || target.colorways.find((c) => c.available) || target.colorways[0];
      a.state[cat] = { family: target.key, handle: cw.handle, variantId: this.resolveSize(cw, a.pref[cat]) };
    });
    this.renderAthlete(a);
    this.afterChange();
  }
  matchFamily(pick, fromGender, a, cat) {
    const fams = this.familiesFor(a, cat);
    if (!fams.length) return null;
    const srcFam = pick ? this.family(pick.family) : null;
    if (!srcFam) return fams[0];
    if (fams.includes(srcFam)) return srcFam; // same range — same product
    const to = a.state.gender;
    let want = srcFam.photoKey;
    if (fromGender === 'men' && to === 'women') want = this.matchMap.m2w[want] || want;
    else if (fromGender === 'women' && to === 'men') want = this.matchMap.w2m[want] || want;
    return fams.find((f) => f.photoKey === want) || fams.find((f) => f.photoKey === srcFam.photoKey) || fams[0];
  }
  afterChange() { this.renderBar(); this.persist(); }

  /* ---------- rendering ---------- */
  renderAthlete(a) {
    a.el.querySelectorAll('[data-gender]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.gender === a.state.gender)));
    ['top', 'bottom'].forEach((cat) => this.renderStep(a, cat));
    this.renderFigure(a);
  }

  renderStep(a, cat) {
    const step = a.el.querySelector(`[data-step="${cat}"]`);
    if (!step) return;
    const fams = this.familiesFor(a, cat);
    const pick = a.state[cat];
    const cw = this.colorway(pick);
    const variant = this.variant(pick);

    step.querySelector('[data-families]').innerHTML = fams.map((f) => {
      // every card shows the colour that is currently chosen (its own colourway for the
      // picked family, the same colour name for the others) — one consistent colour across the row
      const wantColor = cw?.color?.toLowerCase();
      const first = f.colorways.find((c) => c.handle === pick?.handle) || f.colorways.find((c) => c.color.toLowerCase() === wantColor) || f.colorways[0];
      const prices = f.colorways.map((c) => c.price);
      const min = Math.min(...prices), max = Math.max(...prices);
      return `<button type="button" class="dbl__family" role="radio" data-family="${esc(f.key)}" data-category="${cat}" aria-checked="${pick?.family === f.key}">
        <span class="dbl__family-thumb">${first.thumb ? `<img src="${esc(first.thumb)}" alt="" loading="lazy">` : ''}</span>
        <span class="dbl__family-name">${esc(f.label)}<span class="dbl__family-price">${min === max ? money(min) : `${money(min)} - ${money(max)}`}</span></span>
      </button>`;
    }).join('') || '<p class="dbl__empty">Nothing in this range yet.</p>';

    const colors = step.querySelector('[data-colors]');
    const fam = pick ? this.family(pick.family) : null;
    colors.innerHTML = fam ? fam.colorways.map((c) => `
      <button type="button" class="dbl__color" role="radio" data-color="${esc(c.handle)}" data-category="${cat}" aria-checked="${c.handle === pick.handle}" data-soldout="${!c.available}" aria-label="${esc(c.color)}" title="${esc(c.color)}">
        ${c.thumb ? `<img src="${esc(c.thumb)}" alt="" loading="lazy">` : ''}
      </button>`).join('') : '';
    step.querySelector('[data-color-name]').textContent = cw ? cw.color : '';

    const sizes = step.querySelector('[data-sizes]');
    sizes.innerHTML = cw ? this.sizesOf(cw).map((v) => `
      <button type="button" class="dbl__size" role="radio" data-size="${v.id}" data-category="${cat}" aria-checked="${variant?.id === v.id}" ${v.available ? '' : 'disabled'}>${esc(v.size)}</button>`).join('') : '';
    sizes.classList.remove('is-missing');
    step.querySelector('[data-size-name]').textContent = variant ? variant.size : '';
    step.querySelector('[data-step-pick]').textContent = cw ? `${cw.family} · ${cw.color}${variant ? ` · ${variant.size}` : ''} · ${money(variant ? variant.price : cw.price)}` : '';
  }

  renderFigure(a) {
    const fig = a.el.querySelector('[data-figure]');
    if (!fig) return;
    let total = 0; const names = [];
    ['top', 'bottom'].forEach((cat) => {
      const cw = this.colorway(a.state[cat]);
      const v = this.variant(a.state[cat]);
      if (cw) { total += v ? v.price : cw.price; names.push(cw.family); }
    });
    fig.querySelector('[data-figure-kit]').textContent = names.join(' + ');
    fig.querySelector('[data-figure-price]').textContent = total ? money(total) : '';
    this.renderOutfit(a, fig);
  }

  /* The two stacked product photos are the fallback: they are only
     downloaded when this combination has no outfit photo, so the outfit
     picture is never replaced by product shots mid-flight. */
  renderStacked(a, fig) {
    ['top', 'bottom'].forEach((cat) => {
      const slot = fig.querySelector(`[data-slot="${cat}"]`);
      const cw = this.colorway(a.state[cat]);
      this.swapImage(slot, cw?.image || '', cw ? `${cw.title}` : '');
    });
  }

  /* One photo of the whole kit from Shopify Files. File name:
       {gender}_{topKey}_{bottomKey}_{topColour}_{bottomColour}.{ext}
     e.g. men_tee_comp_black_teal.webp · women_bra_short_teal_black.webp
     The gender prefix keeps the two ranges apart — both use tank/short/comp.
     Names without the prefix still work, and any of webp/png/jpg/jpeg is
     accepted, so the merchant cannot get the file type wrong.
     Returns a stable key ("gender|name"); outfitCandidates turns it into URLs. */
  outfitUrl(a) {
    if (!this.outfit) return null;
    const top = this.colorway(a.state.top), bottom = this.colorway(a.state.bottom);
    if (!top || !bottom) return null;
    const ft = this.family(a.state.top.family), fb = this.family(a.state.bottom.family);
    return this.outfitKeyFor(a.state.gender, ft, fb, top, bottom);
  }
  outfitKeyFor(athleteGender, ft, fb, top, bottom) {
    if (!this.outfit || !ft?.photoKey || !fb?.photoKey || !top?.colorKey || !bottom?.colorKey) return null;
    const gender = [ft.gender, fb.gender, athleteGender].find((g) => g && g !== 'unisex') || '';
    return `${gender}|${ft.photoKey}_${fb.photoKey}_${top.colorKey}_${bottom.colorKey}`;
  }
  outfitCandidates(key) {
    const [gender, name] = key.split('|');
    const pref = (this.dataset.outfitExt || 'webp').toLowerCase();
    const exts = [...new Set([pref, 'webp', 'png', 'jpg', 'jpeg'])];
    const names = gender ? [`${gender}_${name}`, name] : [name];
    const urls = [];
    names.forEach((n) => exts.forEach((e) => urls.push(`${this.dataset.filesBase}${n}.${e}?width=1400`)));
    return urls;
  }
  renderOutfit(a, fig) {
    const slot = fig.querySelector('[data-slot="outfit"]');
    const url = this.outfitUrl(a);
    if (!slot || !url) { fig.classList.remove('has-outfit'); this.renderStacked(a, fig); return; }
    const state = this.outfitCache.get(url);
    if (state === 'missing') { fig.classList.remove('has-outfit'); this.renderStacked(a, fig); return; }
    if (state) { fig.classList.add('has-outfit'); this.swapImage(slot, state, ''); return; }
    let candidates = this.outfitCandidates(url);
    const preloaded = fig.dataset.defaultSrc;
    if (preloaded && candidates.includes(preloaded)) candidates = [preloaded, ...candidates.filter((c) => c !== preloaded)];

    // The photo the page started loading itself: reveal it the moment it is
    // ready (no second request, no waiting for JS), and if that file is not
    // there, drop it while it is still invisible and look for another.
    const img = slot.querySelector('[data-slot-img]');
    const initial = img && img.dataset.initial === '1' ? img.getAttribute('src') : null;
    if (initial && candidates.includes(initial)) {
      img.dataset.initial = '';
      const win = () => {
        this.outfitCache.set(url, initial);
        if (this.outfitUrl(a) !== url) return;
        slot.dataset.pending = initial;
        img.style.transition = 'none'; // first paint appears, it does not fade in
        slot.classList.remove('is-loading');
        slot.classList.add('has-image');
        fig.classList.add('has-outfit');
        requestAnimationFrame(() => { img.style.transition = ''; });
      };
      const lose = () => {
        img.removeAttribute('src');
        this.probeOutfit(a, fig, slot, url, candidates.filter((c) => c !== initial));
      };
      if (img.complete) { if (img.naturalWidth > 0) win(); else lose(); return; }
      img.addEventListener('load', win, { once: true });
      img.addEventListener('error', lose, { once: true });
      return;
    }
    this.probeOutfit(a, fig, slot, url, candidates);
  }

  /* Look for the photo without ever showing a half state: the expected file
     name first, and only if that is not there the remaining spellings, all at
     once — so a missing combination costs two round trips, not eight.
     The shimmer runs for the whole search. */
  probeOutfit(a, fig, slot, url, candidates) {
    slot.dataset.probe = url;
    if (!candidates.length) { this.outfitMissing(a, fig, slot, url); return; }
    if (!slot.classList.contains('has-image')) slot.classList.add('is-loading');
    const load = (src) => new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve(src);
      probe.onerror = () => resolve(null);
      probe.src = src;
    });
    const done = (src) => {
      if (slot.dataset.probe !== url || this.outfitUrl(a) !== url) return;
      if (!src) { this.outfitMissing(a, fig, slot, url); return; }
      this.outfitCache.set(url, src);
      fig.classList.add('has-outfit');
      this.swapImage(slot, src, '');
    };
    load(candidates[0]).then((hit) => {
      if (hit) return done(hit);
      const rest = candidates.slice(1);
      if (!rest.length) return done(null);
      return Promise.all(rest.map(load)).then((all) => done(all.find(Boolean) || null));
    });
  }
  outfitMissing(a, fig, slot, url) {
    this.outfitCache.set(url, 'missing');
    slot.classList.remove('is-loading');
    if (this.outfitUrl(a) !== url) return;
    fig.classList.remove('has-outfit');
    this.renderStacked(a, fig);
  }

  /* Photo swap without a single frame of a broken/blank image: the incoming
     photo is fully downloaded off-screen first; only then is it shown —
     instantly on first paint, as a crossfade afterwards. A failed download
     keeps the previous photo (or the empty placeholder). */
  swapImage(slot, src, alt) {
    const cur = slot.querySelector('[data-slot-img]');
    const next = slot.querySelector('[data-slot-next]');
    slot.dataset.pending = src || '';
    if (!src) { slot.classList.remove('has-image', 'is-loading'); cur.removeAttribute('src'); return; }
    if (cur.getAttribute('src') === src) { slot.classList.add('has-image'); slot.classList.remove('is-loading'); return; }
    if (!slot.classList.contains('has-image')) slot.classList.add('is-loading');
    const loader = new Image();
    loader.decoding = 'async';
    loader.onload = () => {
      if (slot.dataset.pending !== src) return; // a newer pick won
      // The photos stay decorative (alt=""): the kit is named in the caption
      // under the picture, and an <img> with alt="" never paints the
      // browser's broken-image icon, whatever state it is in.
      const show = () => {
        slot.classList.remove('is-loading');
        if (!slot.classList.contains('has-image') || reduced.matches) {
          cur.alt = ''; cur.src = src;
          slot.classList.add('has-image');
          return;
        }
        next.alt = ''; next.src = src;
        slot.classList.add('is-swapping');
        setTimeout(() => {
          if (slot.dataset.pending !== src) return;
          // Hand over to the base image only once it really shows the new
          // photo: the overlay stays on top until then, so the moment the
          // base image reloads (even from cache) is never visible.
          const settle = () => {
            if (slot.dataset.pending !== src) return;
            slot.classList.remove('is-swapping');
            next.removeAttribute('src');
          };
          cur.alt = ''; cur.src = src;
          if (cur.complete && cur.naturalWidth > 0) settle();
          else {
            cur.addEventListener('load', settle, { once: true });
            cur.addEventListener('error', settle, { once: true });
          }
        }, 460);
      };
      (loader.decode ? loader.decode().catch(() => {}) : Promise.resolve()).then(show);
    };
    loader.onerror = () => { if (slot.dataset.pending === src) slot.classList.remove('is-loading'); };
    loader.src = src;
  }

  renderBar() {
    if (!this.bar) return;
    const picks = [];
    const missing = [];
    let total = 0, compare = 0, count = 0;
    this.athletes.forEach((a) => ['top', 'bottom'].forEach((cat) => {
      const cw = this.colorway(a.state[cat]);
      const v = this.variant(a.state[cat]);
      if (!cw) return;
      count += 1;
      total += v ? v.price : cw.price;
      compare += v && v.compare > (v.price) ? v.compare : (v ? v.price : cw.price);
      picks.push({ cw, v, a, cat });
      if (!v) missing.push(`${a.name} · ${cat}`);
    }));
    const items = this.bar.querySelector('[data-bar-items]');
    items.innerHTML = this.athletes.map((a) => `<div class="dbl__sum"><span class="dbl__sum-name">${esc(a.name)}</span>${['top', 'bottom'].map((cat) => {
      const cw = this.colorway(a.state[cat]); const v = this.variant(a.state[cat]);
      if (!cw) return `<span class="dbl__sum-line"><span></span><span class="dbl__sum-text"><span class="is-missing">${cat === 'top' ? 'No top chosen' : 'No bottom chosen'}</span></span><span></span></span>`;
      return `<span class="dbl__sum-line">${cw.thumb ? `<img src="${esc(cw.thumb)}" alt="" width="28" height="28">` : '<span></span>'}<span class="dbl__sum-text">${esc(cw.family)} · ${esc(cw.color)} · ${v ? `<b>${esc(v.size)}</b>` : '<span class="is-missing">size?</span>'}</span><span class="dbl__sum-price">${money(v ? v.price : cw.price)}</span></span>`;
    }).join('')}</div>`).join('');
    this.bar.querySelector('[data-bar-total]').textContent = total ? money(total) : '';
    this.bar.querySelector('[data-bar-count]').textContent = count ? `· ${count} pieces` : '';
    const save = this.bar.querySelector('[data-bar-save]');
    if (compare > total) { save.hidden = false; save.textContent = `You save ${money(compare - total)}`; } else save.hidden = true;
    const hint = this.bar.querySelector('[data-bar-hint]');
    const ready = count === 4 && missing.length === 0;
    hint.textContent = ready ? '' : missing.length ? `Choose a size for: ${missing.join(', ')}` : 'Pick a top and a bottom for both athletes.';
    this.addBtn.disabled = !ready;
    this.picks = picks;
  }

  /* ---------- cart ---------- */
  async addKit() {
    if (this.addBtn.disabled || !this.picks?.length) return;
    // highlight anything missing (defensive — the button is disabled otherwise)
    const items = this.picks.map(({ v, a, cat }) => ({
      id: v.id, quantity: 1,
      properties: { Kit: 'Doubles', Athlete: a.name, Piece: cat === 'top' ? 'Top' : 'Bottom' },
    }));
    this.addBtn.classList.add('btn--loading');
    this.addBtn.disabled = true;
    try {
      const code = this.dataset.discount?.trim();
      if (code) { try { await fetch(`${Theme.routes.root || '/'}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }); } catch { /* optional */ } }
      await Theme.Cart.add(items, { open: false });
      document.dispatchEvent(new CustomEvent('doubles:kit_added', { detail: { item_count: items.length } }));
      this.addBtn.classList.add('is-added');
      this.addBtn.textContent = Theme.strings.added || 'Added to cart';
      if (this.dataset.cartIcon === 'page') { window.location.href = `${Theme.routes.root || '/'}cart`; return; }
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) drawer.open(this.addBtn); else window.location.href = `${Theme.routes.root || '/'}cart`;
      setTimeout(() => { this.addBtn.classList.remove('is-added'); this.addBtn.textContent = this.addLabel; this.addBtn.disabled = false; }, 2600);
    } catch (err) {
      Theme.toast?.(err?.message || Theme.strings.cartError || 'Could not add the kit');
      this.addBtn.disabled = false;
    } finally {
      this.addBtn.classList.remove('btn--loading');
    }
  }

  /* ---------- URL state (shareable kit) ---------- */
  persist() {
    const enc = (a) => [a.state.gender, ...['top', 'bottom'].map((c) => a.state[c] ? `${a.state[c].family}.${a.state[c].handle}.${a.state[c].variantId || ''}` : '')].join('~');
    const params = new URLSearchParams(window.location.search);
    const kit = this.athletes.map(enc).join('__');
    params.set('kit', kit);
    history.replaceState(null, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
    try { localStorage.setItem('dbl-kit', JSON.stringify({ t: Date.now(), kit })); } catch { /* private mode */ }
  }
  /* A kit in the link wins (shared link); otherwise the visitor's own kit
     comes back for two weeks. A first visit starts on the default kit the
     page was rendered with. */
  restore() {
    let kit = new URLSearchParams(window.location.search).get('kit');
    if (!kit) {
      try {
        const saved = JSON.parse(localStorage.getItem('dbl-kit') || 'null');
        if (saved && saved.kit && Date.now() - saved.t < 14 * 86400000) kit = saved.kit;
      } catch { /* private mode */ }
    }
    if (!kit) return;
    kit.split('__').forEach((chunk, i) => {
      const a = this.athletes[i]; if (!a) return;
      const [gender, top, bottom] = chunk.split('~');
      if (gender) a.state.gender = gender;
      [['top', top], ['bottom', bottom]].forEach(([cat, val]) => {
        if (!val) return;
        const [family, handle, variantId] = val.split('.');
        if (this.family(family)?.colorways.some((c) => c.handle === handle)) {
          a.state[cat] = { family, handle, variantId: Number(variantId) || null };
          a.pref[cat] = this.variant(a.state[cat])?.size || null;
          if (!a.state[cat].variantId) a.state[cat].variantId = this.resolveSize(this.colorway(a.state[cat]), null);
        }
      });
    });
  }
}
if (!customElements.get('doubles-builder')) customElements.define('doubles-builder', DoublesBuilder);
